/** Calendar dates (YYYY-MM-DD) handled in UTC, so they do not depend on the extranet user's time zone. */

export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const WEEKDAYS_LONG = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const todayIso = () => new Date().toISOString().slice(0, 10);

export function addDays(date: string, n: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/** 1 = Monday … 7 = Sunday. */
export function weekdayOf(date: string): number {
  const js = new Date(`${date}T00:00:00Z`).getUTCDay();
  return js === 0 ? 7 : js;
}

export function startOfWeek(date: string): string {
  return addDays(date, 1 - weekdayOf(date));
}

export function startOfMonth(date: string): string {
  return `${date.slice(0, 7)}-01`;
}

export function endOfMonth(date: string): string {
  return addDays(startOfMonth(addDays(startOfMonth(date), 32)), -1);
}

export function eachDay(from: string, to: string): string[] {
  const out: string[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) out.push(d);
  return out;
}

export const dayMonth = (date: string) => `${Number(date.slice(8, 10))} ${MONTHS[Number(date.slice(5, 7)) - 1]}`;
export const monthYear = (date: string) => `${MONTHS[Number(date.slice(5, 7)) - 1]} ${date.slice(0, 4)}`;

export function longDate(date: string): string {
  return `${WEEKDAYS_LONG[weekdayOf(date) - 1]}, ${Number(date.slice(8, 10))} ${["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"][Number(date.slice(5, 7)) - 1]}`;
}

/** Relative to today, for arrival lists: "today", "tomorrow", "in 5 days" or the date. */
export function relativeDay(date: string, today = todayIso()): string {
  const n = daysBetween(today, date);
  if (n === 0) return "today";
  if (n === 1) return "tomorrow";
  if (n === -1) return "yesterday";
  if (n > 1 && n < 7) return `in ${n} days`;
  return dayMonth(date);
}

export const dateTime = (iso?: string, timeZone?: string) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short", ...(timeZone ? { timeZone } : {}) }) : "—");

export function money(minor: number, currency = "EUR", opts: { decimals?: boolean } = {}): string {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency, ...(opts.decimals === false ? { maximumFractionDigits: 0 } : {}) }).format(minor / 100);
}

/** Minor-unit amount shown in an input (13250 → "132.5"). */
export const minorToInput = (minor?: number) => (minor === undefined ? "" : String(minor / 100));

/** "132,50" or "132.5" → 13250; empty or invalid → undefined. */
export function inputToMinor(text: string): number | undefined {
  const t = text.trim().replace(",", ".");
  if (t === "") return undefined;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : undefined;
}

export const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
export const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join("");

/** Lower case without accents, for matching what someone typed: "sao jorge" finds "São Jorge". */
export const foldText = (s: string) => s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase().trim();
